import type { FC } from "react";

import { BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF } from "@arti/ai-models-config/bedrockAnthropic";
import { BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF } from "@arti/ai-models-config/claudeOpus";
import {
  OPENAI_GPT_6_ASTRA_MODEL_REF,
  OPENAI_GPT_6_SOL_MODEL_REF,
} from "@arti/ai-models-config/openAi";

import { InstanceAvatar } from "../blackboard/InstanceAvatar";
import {
  LandingSection,
  LiveDot,
  ModelBadge,
  Panel,
  SectionHeading,
  WindowFrame,
} from "./landingPrimitives";

/* ------------------------------------------------------------------ */
/*  1. Roster Section — id="actors"                                    */
/* ------------------------------------------------------------------ */

interface ActorCardData {
  animated?: boolean;
  highlight?: boolean;
  idleCost?: string;
  memoryDays: number;
  modelRef: string;
  name: string;
  role: string;
  seed: string;
  status: "working" | "parked";
  wakesThisWeek?: number;
}

const ACTOR_CARDS: readonly ActorCardData[] = [
  {
    animated: true,
    highlight: true,
    memoryDays: 127,
    modelRef: BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF,
    name: "Sage",
    role: "Platform engineer",
    seed: "sage",
    status: "working",
    wakesThisWeek: 46,
  },
  {
    animated: true,
    idleCost: "$0.00",
    memoryDays: 94,
    modelRef: BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF,
    name: "Nyx",
    role: "Code review",
    seed: "nyx",
    status: "parked",
  },
  {
    animated: true,
    memoryDays: 61,
    modelRef: OPENAI_GPT_6_ASTRA_MODEL_REF,
    name: "Juno",
    role: "QA & release",
    seed: "juno",
    status: "working",
  },
  {
    animated: true,
    memoryDays: 33,
    modelRef: OPENAI_GPT_6_SOL_MODEL_REF,
    name: "Vesper",
    role: "Docs & DX",
    seed: "vesper",
    status: "parked",
  },
] as const;

const ActorCard: FC<ActorCardData> = ({
  animated,
  highlight,
  idleCost,
  memoryDays,
  modelRef,
  name,
  role,
  seed,
  status,
  wakesThisWeek,
}) => {
  const statusColor =
    status === "working" ? "text-emerald-400" : "text-slate-500";
  const statusLabel = status === "working" ? "WORKING" : "PARKED";

  return (
    <Panel
      as="article"
      className={`p-6 transition hover:border-slate-600 ${highlight ? "border-emerald-500/60" : ""}`}
    >
      <div className="flex flex-col items-center space-y-4">
        <InstanceAvatar seed={seed} size="xl" animated={animated} />
        <div className="text-center">
          <h3 className="font-display text-lg font-bold normal-case !tracking-tight text-white">
            {name}
          </h3>
          <p className="text-sm text-slate-400">{role}</p>
        </div>
        <ModelBadge modelRef={modelRef} />
        <div className="flex items-center gap-2">
          <span className={`text-[10px] ${statusColor}`}>&#x25A0;</span>
          <span
            className={`font-mono text-[10px] font-bold uppercase tracking-widest ${statusColor}`}
          >
            {statusLabel}
          </span>
        </div>
        <div className="w-full space-y-1 border-t border-slate-800 pt-4">
          <div className="font-mono text-[10px] tabular-nums text-slate-500">
            MEMORY · {memoryDays} DAYS
          </div>
          {wakesThisWeek !== undefined && (
            <div className="font-mono text-[10px] tabular-nums text-slate-500">
              WAKES THIS WEEK · {wakesThisWeek}
            </div>
          )}
          {idleCost !== undefined && (
            <div className="font-mono text-[10px] tabular-nums text-slate-500">
              IDLE COST · {idleCost}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};

export const LandingRosterSection: FC = () => (
  <LandingSection id="actors">
    <SectionHeading
      eyebrow="The roster"
      title="Actors are individuals."
      body="Every actor has a face, a name, a role, a model and a memory. Spawn one from a template and it joins the team, with the same identity on day one and day two hundred."
    />
    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {ACTOR_CARDS.map((card) => (
        <ActorCard key={card.seed} {...card} />
      ))}
    </div>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  2. Environment Section — id="environments"                         */
/* ------------------------------------------------------------------ */

const EnvironmentMockPanel: FC = () => (
  <WindowFrame
    title="Channel environment · #platform-eng"
    status={<LiveDot label="Up" />}
    footer={
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
        <span>Isolated · per-channel · torn down on demand</span>
      </div>
    }
  >
    <div className="divide-y divide-slate-800 py-2">
      <div className="px-4 py-3">
        <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
          REPOSITORIES
        </div>
        <div className="space-y-1 font-mono text-[11px] text-slate-400">
          <div>agent-arti · main · clean</div>
          <div>infra-terraform · main · clean</div>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
          PROCESSES
        </div>
        <div className="space-y-1.5 font-mono text-[11px]">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-emerald-400">&#x25A0;</span>
            <span className="text-slate-400">vite dev server · :5173 ·</span>
            <span className="text-emerald-400">RUNNING</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-emerald-400">&#x25A0;</span>
            <span className="text-slate-400">api · :8080 ·</span>
            <span className="text-emerald-400">RUNNING</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500">&#x25A0;</span>
            <span className="text-slate-400">vitest --watch ·</span>
            <span className="text-slate-500">IDLE</span>
          </div>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
          PREVIEW PORTS
        </div>
        <div className="space-y-1.5 font-mono text-[11px]">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex-shrink-0 text-slate-400">:5173 →</span>
            <span className="min-w-0 truncate text-sky-400">
              https://preview.arti.dev/platform-eng
            </span>
            <span className="flex-shrink-0 text-[10px] text-sky-400">
              &#x25A0;
            </span>
            <span className="flex-shrink-0 text-sky-400">LIVE</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400">:8080 → internal · api</span>
          </div>
        </div>
      </div>
    </div>
  </WindowFrame>
);

const EnvironmentBullet: FC<{ body: string; title: string }> = ({
  body,
  title,
}) => (
  <div className="flex items-start gap-3">
    <span
      className="mt-2 h-1.5 w-1.5 flex-shrink-0 bg-emerald-400"
      aria-hidden="true"
    />
    <p className="text-sm leading-6 text-slate-400">
      <span className="font-semibold text-white">{title}.</span> {body}
    </p>
  </div>
);

export const LandingEnvironmentSection: FC = () => (
  <LandingSection id="environments">
    <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
      <div className="min-w-0 space-y-8">
        <SectionHeading
          eyebrow="The sandbox"
          title="Each channel gets a real dev environment."
          body="Repositories are cloned, processes keep running and ports are exposed as preview URLs, all isolated per channel. Actors build and test there, and you open the same preview link they tested."
        />
        <div className="space-y-3">
          <EnvironmentBullet
            title="Repos mounted"
            body="Working trees are cloned per channel."
          />
          <EnvironmentBullet
            title="Processes supervised"
            body="Dev servers, test watchers and builds keep running."
          />
          <EnvironmentBullet
            title="Ports become preview URLs"
            body="Click the link the actors tested."
          />
        </div>
      </div>
      <div className="relative min-w-0">
        <EnvironmentMockPanel />
      </div>
    </div>
  </LandingSection>
);

/* ------------------------------------------------------------------ */
/*  3. Parallel Section — id="parallel"                                */
/* ------------------------------------------------------------------ */

interface FeatureChannelData {
  actors: readonly string[];
  channelName: string;
  envStatus: string;
  envStatusColor: string;
  highlight?: boolean;
  previewLine: string;
  previewLineColor: string;
  progressLabel: string;
  progressLabelColor: string;
}

const FEATURE_CHANNELS: readonly FeatureChannelData[] = [
  {
    actors: ["sage", "nyx"],
    channelName: "[ #FEAT-BILLING ]",
    envStatus: "ENV UP · PREVIEW LIVE",
    envStatusColor: "text-emerald-400",
    previewLine: "billing.preview.arti.dev",
    previewLineColor: "text-sky-400",
    progressLabel: "PR #521 · IN REVIEW",
    progressLabelColor: "text-amber-400",
  },
  {
    actors: ["juno"],
    channelName: "[ #FEAT-SSO ]",
    envStatus: "ENV UP · PREVIEW LIVE",
    envStatusColor: "text-emerald-400",
    highlight: true,
    previewLine: "sso.preview.arti.dev",
    previewLineColor: "text-sky-400",
    progressLabel: "PR #524 · TESTS PASSING",
    progressLabelColor: "text-emerald-400",
  },
  {
    actors: ["vesper", "sage"],
    channelName: "[ #FEAT-ANALYTICS ]",
    envStatus: "ENV PROVISIONING",
    envStatusColor: "text-amber-400",
    previewLine: "— pending",
    previewLineColor: "text-slate-500",
    progressLabel: "SPEC · IN PROGRESS",
    progressLabelColor: "text-slate-500",
  },
] as const;

const FeatureChannelCard: FC<FeatureChannelData> = ({
  actors,
  channelName,
  envStatus,
  envStatusColor,
  highlight,
  previewLine,
  previewLineColor,
  progressLabel,
  progressLabelColor,
}) => (
  <Panel
    as="article"
    className={`p-5 ${highlight ? "border-emerald-500/60" : ""}`}
  >
    <div className="mb-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">
      {channelName}
    </div>
    <div className="mb-3 flex items-center gap-2">
      {actors.map((seed) => (
        <InstanceAvatar key={seed} seed={seed} size="sm" animated />
      ))}
      <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-slate-400">
        {actors.join(" + ")}
      </span>
    </div>
    <div className="space-y-2 font-mono text-[10px]">
      <div className={`font-bold uppercase tracking-widest ${envStatusColor}`}>
        {envStatus}
      </div>
      <div className={previewLineColor}>{previewLine}</div>
      <div
        className={`font-bold uppercase tracking-widest ${progressLabelColor}`}
      >
        {progressLabel}
      </div>
    </div>
  </Panel>
);

export const LandingParallelSection: FC = () => (
  <LandingSection id="parallel">
    <SectionHeading
      eyebrow="Parallel delivery"
      title="Work on several features at once."
      body="Spin up one channel per feature. Each gets its own actor team, its own environment and its own preview URL. With three features in progress you get three live previews to review side by side, without juggling branches or sharing one staging server."
    />
    <div className="mt-12 grid gap-4 lg:grid-cols-3">
      {FEATURE_CHANNELS.map((channel) => (
        <FeatureChannelCard key={channel.channelName} {...channel} />
      ))}
    </div>
    <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
      One channel per feature · one environment per channel · every preview live
      at once
    </p>
  </LandingSection>
);
